import Modal from '@mui/material/Modal';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cn, scrollToTop } from '../../../../utils';
import {
  cityFilters,
  figureFilters,
  settingFilters,
  sort_data,
  starSignFilters,
} from '../../../../utils/loveFilters';
import {
  getPreferredUsers,
  resetPreferredUsers,
  setHasMoreLoveUsers,
  setPreferredUsersParams,
  setSkip,
} from '../../../store/slices/community/loveSlice';
import CustomDropdown from '../../Common/CustomDropdown/CustomDropdown';
import CustomDropdownForRangeSlider from '../../Common/CustomDropdown/CustomDropdownForRangeSlider';
import OutsideAlerter from '../../Common/OutsideAlerter/OutsideAlerter';

const MdFilterList = dynamic(() =>
  import('react-icons/md').then((mod) => mod.MdFilterList)
);

const FilterView = () => {
  const lang = useSelector(({ common }) => common?.language);
  const dispatch = useDispatch();

  const didMount = useRef(null);

  const [age, setAge] = useState([18, 100]);
  const [height, setHeight] = useState([100, 300]);
  const [setting, setSetting] = useState(settingFilters(lang)[0]);
  const [figure, setFigure] = useState(figureFilters(lang)[0]);
  const [starSign, setStarSign] = useState(starSignFilters(lang)[0]);
  const [city, setCity] = useState(cityFilters(lang)[0]);
  const [selectedSort, setSelectedSort] = useState(sort_data(lang)[0]);

  const [showAgeSlider, setShowAgeSlider] = useState(false);
  const [showHeightSlider, setShowHeightSlider] = useState(false);
  const [applyAgeFilter, setApplyAgeFilter] = useState(false);
  const [applyHeightFilter, setApplyHeightFilter] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [foundNewMatch, setFoundNewMatch] = useState(false);
  const [isShowAllMatchesSelected, setIsShowAllMatchesSelected] =
    useState(false);
  const [isShowAllLikesSelected, setIsShowAllLikesSelected] = useState(false);

  const isCurrentUserRegistered = useSelector(
    ({ love }) => love?.isCurrentUserRegistered
  );

  const topNotifications = useSelector(
    ({ notifications }) => notifications?.topNotifications
  );

  function getSortingValue() {
    if (selectedSort?.id === 2) return 2;
    else return 1;
  }

  function getLookingForFilterValue() {
    if (setting?.value === 'open_relationship') return 1;
    else if (setting?.value === 'serious_relationship') return 2;
    else return 'all';
  }

  function getCityIdForFilter() {
    return city.id === 9999 ? isCurrentUserRegistered?.city_id : city.id;
  }

  function fetchDataAfterFilter(params) {
    dispatch(getPreferredUsers(params));
  }

  const params = {
    figure: figure.value,
    looking_for: getLookingForFilterValue(),
    min_age: age[0],
    max_age: age[1],
    min_height: height[0],
    max_height: height[1],
    sorting: getSortingValue(),
    star_sign: starSign.id,
    city_id: getCityIdForFilter(),
    all_results: city.id === 9999 ? 1 : 0,
    take: 10,
    skip: 0,
  };

  const default_params = {
    sorting: 1,
    figure: figureFilters(lang)[0].value,
    looking_for: 'all',
    min_age: 18,
    max_age: 100,
    min_height: 100,
    max_height: 300,
    star_sign: starSignFilters(lang)[0].value,
    city_id: isCurrentUserRegistered?.city_id,
    all_results: city.id === 9999 ? 1 : 0,
    take: 10,
    skip: 0,
  };

  function filterAge() {
    // fetchDataAfterFilter(params);
    setApplyAgeFilter(false);
  }

  function filterHeight() {
    // fetchDataAfterFilter(params);
    setApplyHeightFilter(false);
  }

  function filterOthers() {
    dispatch(resetPreferredUsers());
    dispatch(setPreferredUsersParams(params));
    dispatch(setHasMoreLoveUsers(true));
    dispatch(setSkip(0));
    fetchDataAfterFilter(params);
    scrollToTop();
  }

  function sortData() {
    dispatch(resetPreferredUsers());
    dispatch(setPreferredUsersParams(params));
    fetchDataAfterFilter(params);
    scrollToTop();
  }

  function filterShowAllMatches() {
    default_params['show_matches'] = 1;
    default_params['show_likes'] = 0;
    dispatch(setPreferredUsersParams(default_params));
    dispatch(resetPreferredUsers());
    fetchDataAfterFilter(default_params);
    setFoundNewMatch(false);
    setIsShowAllMatchesSelected(true);
    setModalOpen(false);
    scrollToTop();
  }

  function filterShowAllLikes() {
    default_params['show_matches'] = 0;
    default_params['show_likes'] = 1;
    dispatch(setPreferredUsersParams(default_params));
    dispatch(resetPreferredUsers());
    fetchDataAfterFilter(default_params);
    setIsShowAllLikesSelected(true);
    setModalOpen(false);
    scrollToTop();
  }

  function clearFilters() {
    setFigure(figureFilters(lang)[0]);
    setSetting(settingFilters(lang)[0]);
    setAge([18, 100]);
    setHeight([100, 300]);
    setStarSign(starSignFilters(lang)[0]);
    setCity(cityFilters(lang)[0]);
    setSelectedSort(sort_data(lang)[0]);
    fetchDataAfterFilter(default_params);
    setIsShowAllLikesSelected(false);
    setIsShowAllMatchesSelected(false);
    setModalOpen(false);
    dispatch(setHasMoreLoveUsers(true));
    dispatch(setPreferredUsersParams(default_params));
    dispatch(resetPreferredUsers());
    scrollToTop();
  }

  function checkForNewMatch() {
    if (topNotifications?.length > 0) {
      for (const item of topNotifications) {
        if (
          item?.notification_type === 'love_new_match' &&
          item?.read_at === null
        ) {
          setFoundNewMatch(true);
          return;
        }
      }
    }
    setFoundNewMatch(false);
    return;
  }

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleApply = () => {
    filterOthers();
    setModalOpen(!modalOpen);
  };

  useEffect(() => {
    if (applyAgeFilter) {
      filterAge();
    }
  }, [applyAgeFilter]);

  useEffect(() => {
    if (applyHeightFilter) {
      filterHeight();
    }
  }, [applyHeightFilter]);

  useEffect(() => {
    if (didMount.current) {
      // filterOthers();
    } else {
      didMount.current = true;
    }
  }, [figure, setting, starSign, city?.id]);

  useEffect(() => {
    if (didMount.current && selectedSort?.id) {
      sortData();
    } else {
      didMount.current = true;
    }
  }, [selectedSort?.id]);

  useEffect(() => {
    checkForNewMatch();
  }, [topNotifications?.length]);

  return (
    <>
      <div className='hidden justify-center gap-2 sm:flex'>
        {isCurrentUserRegistered?.quiz_taken && (
          <CustomDropdown
            label={lang?.common?.filters}
            dropdownItems={sort_data(lang)}
            selected={selectedSort}
            setSelected={setSelectedSort}
          />
        )}

        <div
          className='flex w-[166px] cursor-pointer items-center justify-center gap-[8px] rounded-[100px] border px-[17px] py-[16px] text-sm font-bold'
          onClick={() => setModalOpen(true)}
        >
          <span className='text-[15px]'>
            <MdFilterList />
          </span>
          <button>{lang?.common?.filters}</button>
        </div>
      </div>

      <Modal
        className='flex items-center justify-center overflow-scroll '
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby='simple-modal-title'
        aria-describedby='simple-modal-description'
      >
        <div className='w-[600px] bg-white px-4 py-4'>
          <div>
            <h4
              className='my-3 text-center font-semibold
            '
            >
              {lang?.common?.filters}
            </h4>
          </div>
          <div className='flex flex-wrap gap-2'>
            <OutsideAlerter outsideClickHandler={setShowAgeSlider}>
              <CustomDropdownForRangeSlider
                label={`${lang?.community?.age} (${age[0]} - ${age[1]}})`}
                age={age}
                setAge={setAge}
                showAgeSlider={showAgeSlider}
                setShowAgeSlider={setShowAgeSlider}
                setApplyAgeFilter={setApplyAgeFilter}
                min={18}
                max={100}
                title={lang?.community?.choose_your_preferred_age_range}
              />
            </OutsideAlerter>

            <CustomDropdown
              label={lang?.common?.filters}
              dropdownItems={settingFilters(lang)}
              selected={setting}
              setSelected={setSetting}
            />

            <CustomDropdown
              label={lang?.common?.filters}
              dropdownItems={cityFilters(lang)}
              selected={city}
              setSelected={setCity}
            />

            <CustomDropdown
              label={lang?.common?.filters}
              dropdownItems={figureFilters(lang)}
              selected={figure}
              setSelected={setFigure}
            />
            <OutsideAlerter outsideClickHandler={setShowHeightSlider}>
              <CustomDropdownForRangeSlider
                label={`${lang?.community?.all_height} (${height[0]} - ${height[1]}})`}
                age={height}
                setAge={setHeight}
                showAgeSlider={showHeightSlider}
                setShowAgeSlider={setShowHeightSlider}
                setApplyAgeFilter={setApplyHeightFilter}
                min={100}
                max={300}
                title={lang?.community?.choose_your_preferred_height_range}
              />
            </OutsideAlerter>

            <CustomDropdown
              label={lang?.common?.filters}
              dropdownItems={starSignFilters(lang)}
              selected={starSign}
              setSelected={setStarSign}
            />

            {isCurrentUserRegistered?.quiz_taken ? (
              <>
                <div
                  onClick={filterShowAllMatches}
                  className={cn(
                    'relative flex items-center rounded-[100px] border px-[17px] py-[8px] text-sm',
                    isShowAllMatchesSelected ? 'bg-black text-white' : ''
                  )}
                >
                  <button>{lang?.community?.show_all_matches}</button>
                  {foundNewMatch && (
                    <div className='absolute right-0 top-2 h-2 w-2 rounded-full bg-blue-600'></div>
                  )}
                </div>
                <div
                  onClick={filterShowAllLikes}
                  className={cn(
                    'flex items-center rounded-[100px] border px-[17px] py-[8px] text-sm',
                    isShowAllLikesSelected ? 'bg-black text-white' : ''
                  )}
                >
                  <button>{lang?.community?.show_all_likes}</button>
                </div>
              </>
            ) : null}
          </div>
          <div className='mt-4 flex items-center justify-between px-4'>
            <button onClick={() => clearFilters()}>
              {lang?.common?.clear}
            </button>
            <div
              onClick={handleApply}
              className='flex cursor-pointer  items-center rounded-[100px] bg-black px-[27px] py-[8px] text-sm text-white'
            >
              <button>{lang?.common?.apply}</button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FilterView;
